import React, { useState, useEffect, useCallback, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import { Color } from "@tiptap/extension-color";
import TextStyle from "@tiptap/extension-text-style";
import FontSize from "tiptap-extension-font-size";
import Paragraph from "@tiptap/extension-paragraph";
import { X } from "lucide-react";

import { templates } from "./resumeTemplateStyles";
import ModernEditorToolbar from "./ModernEditorToolbar";
import MobileEditorToolbar from "./MobileEditorToolbar";
import "./pdfEditorStyles.css";

// US Letter dimensions
const SCREEN_DPI = 96;
const LETTER_WIDTH_IN = 8.5;
const LETTER_HEIGHT_IN = 11;
const LETTER_WIDTH_PX = LETTER_WIDTH_IN * SCREEN_DPI; // 816px
const LETTER_HEIGHT_PX = LETTER_HEIGHT_IN * SCREEN_DPI; // 1056px

// Template-specific margins
const TEMPLATE_MARGINS = {
  classic: {
    top: 50,
    right: 50,
    bottom: 40,
    left: 50,
    editorPaddingBottom: 100,
  },
  modern: { top: 50, right: 50, bottom: 40, left: 50, editorPaddingBottom: 40 },
  executive: {
    top: 40,
    right: 40,
    bottom: 30,
    left: 40,
    editorPaddingBottom: 40,
  },
};

// Custom Paragraph extension that preserves the 'class' attribute
const CustomParagraph = Paragraph.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      class: {
        default: null,
      },
    };
  },
});

const PageEditor = React.memo(
  ({
    pageNumber,
    content,
    onContentChange,
    isLoading,
    templateId,
    selectedTemplate,
    margins,
    isFirstPage = false,
    onSelectionUpdate,
    isMobile = false,
    onToggleFullscreen,
    isFullscreen,
  }) => {
    const [selectionKey, setSelectionKey] = useState(0);
    const [localContent, setLocalContent] = useState(content);
    const updateTimeoutRef = useRef(null);
    const editorRef = useRef(null);
    const isUpdatingRef = useRef(false);

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: { levels: [1, 3] },
          paragraph: false,
        }),
        CustomParagraph,
        Link.configure({ openOnClick: false, autolink: true }),
        TextAlign.configure({
          types: ["heading", "paragraph", "listItem"],
          alignments: ["left", "center", "right"],
          defaultAlignment: "left",
        }),
        TextStyle,
        Color.configure({ types: ["textStyle"] }),
        FontSize.configure({
          types: ["textStyle"],
        }),
      ],
      content: content || "",
      editable: !isLoading,
      onUpdate: ({ editor }) => {
        if (isUpdatingRef.current) return;
        const html = editor.getHTML();
        setLocalContent(html);
        setSelectionKey((k) => k + 1);
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }
        updateTimeoutRef.current = setTimeout(() => {
          onContentChange(pageNumber, html);
        }, 300);
      },
      onSelectionUpdate: ({ editor }) => {
        setSelectionKey((k) => k + 1);
        onSelectionUpdate?.(pageNumber);
      },
      editorProps: {
        attributes: {
          class: "focus:outline-none",
        },
        handleDOMEvents: {
          focus: (view) => {
            editorRef.current = editor;
            return false;
          },
        },
      },
    });

    useEffect(() => {
      if (
        editor &&
        content !== undefined &&
        content !== localContent &&
        !editor.isFocused
      ) {
        isUpdatingRef.current = true;
        editor.commands.setContent(content, false);
        setLocalContent(content);
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 100);
      }
    }, [content, editor, localContent]);

    useEffect(() => {
      editor?.setEditable(!isLoading);
    }, [isLoading, editor]);

    useEffect(() => {
      return () => {
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }
      };
    }, []);

    if (!editor) {
      return (
        <div className="loading-placeholder p-6 text-center text-gray-600 bg-gray-50">
          Loading editor...
        </div>
      );
    }

    // Determine dimensions based on mobile/desktop
    const containerHeight = isMobile ? "auto" : `${LETTER_HEIGHT_PX}px`;
    const containerMinHeight = isMobile ? "600px" : "auto";
    const containerOverflow = isMobile ? "visible" : "hidden";
    const editorPaddingBottom = isMobile ? "40px" : "140px";

    return (
      <>
        {/* Show mobile toolbar on mobile, desktop toolbar on desktop */}
        {isMobile ? (
          pageNumber === 1 && ( // Only show mobile toolbar once, not for each page
            <MobileEditorToolbar
              editor={editor}
              selectionKey={selectionKey}
              onToggleFullscreen={onToggleFullscreen}
              isFullscreen={isFullscreen}
            />
          )
        ) : (
          <ModernEditorToolbar editor={editor} selectionKey={selectionKey} />
        )}

        <div
          className={`resume-template-container template-${templateId}`}
          style={{
            width: "100%",
            height: containerHeight,
            minHeight: containerMinHeight,
            overflow: containerOverflow,
            position: "relative",
          }}
        >
          <EditorContent
            editor={editor}
            className="tiptap-editor-content"
            style={{
              height: containerHeight,
              minHeight: containerMinHeight,
              overflow: containerOverflow,
              paddingBottom: editorPaddingBottom,
            }}
          />
        </div>
      </>
    );
  }
);

// Main Component
function PdfEditorComponent({
  initialContent,
  isLoading,
  onContentChange,
  templateId,
  onPagesDataChange,
}) {
  const [pageContents, setPageContents] = useState([
    {
      pageNumber: 1,
      content: initialContent || "",
      height: 0,
    },
  ]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isMobileFullscreen, setIsMobileFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const measureTimeoutRef = useRef(null);
  const containerRefs = useRef({});

  const selectedTemplate =
    templates.find((t) => t.id === templateId) || templates[0];

  const margins = TEMPLATE_MARGINS[templateId] || TEMPLATE_MARGINS.modern;

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Handle body scroll lock for mobile fullscreen
  useEffect(() => {
    if (isMobileFullscreen) {
      document.body.classList.add("mobile-fullscreen-active");
    } else {
      document.body.classList.remove("mobile-fullscreen-active");
    }

    return () => {
      document.body.classList.remove("mobile-fullscreen-active");
    };
  }, [isMobileFullscreen]);

  const toggleFullscreen = () => {
    setIsMobileFullscreen(!isMobileFullscreen);
  };

  const handlePageContentChange = useCallback(
    (pageNumber, newContent) => {
      console.log("=== PdfEditorComponent: handlePageContentChange ===");
      console.log("Page number:", pageNumber);
      console.log(
        "New content preview:",
        newContent ? newContent.substring(0, 150) + "..." : "empty"
      );

      if (isProcessing) return;

      setPageContents((prev) => {
        const updated = prev.map((page) =>
          page.pageNumber === pageNumber
            ? { ...page, content: newContent }
            : page
        );

        const oldContent =
          prev.find((p) => p.pageNumber === pageNumber)?.content || "";
        const contentLengthDiff = Math.abs(
          newContent.length - oldContent.length
        );
        const hasBlockChange =
          (newContent.includes("<h1") && !oldContent.includes("<h1")) ||
          (newContent.includes("<h3") && !oldContent.includes("<h3")) ||
          (newContent.includes("<ul") && !oldContent.includes("<ul")) ||
          (newContent.includes("<ol") && !oldContent.includes("<ol")) ||
          newContent.split("<p").length !== oldContent.split("<p").length;

        // Only redistribute on desktop or significant changes
        if (!isMobile && (contentLengthDiff > 200 || hasBlockChange)) {
          if (measureTimeoutRef.current) {
            clearTimeout(measureTimeoutRef.current);
          }
          measureTimeoutRef.current = setTimeout(() => {
            redistributeContent(updated);
          }, 2000);
        }

        console.log("Updated pageContents:", updated);
        return updated;
      });

      // Create the combined content
      const allContent = pageContents
        .sort((a, b) => a.pageNumber - b.pageNumber)
        .map((page) =>
          page.pageNumber === pageNumber ? newContent : page.content
        )
        .join("");

      console.log("Combined content length:", allContent.length);
      onContentChange?.(allContent);

      // Pass updated pages data
      const currentPages = pageContents.map((page) =>
        page.pageNumber === pageNumber ? { ...page, content: newContent } : page
      );
      console.log("Calling onPagesDataChange with:", currentPages);
      onPagesDataChange?.(currentPages);
    },
    [isProcessing, pageContents, onContentChange, onPagesDataChange, isMobile]
  );

  const redistributeContent = useCallback(
    async (pages) => {
      // Skip redistribution on mobile
      if (isMobile) return;

      console.log("=== PdfEditorComponent: redistributeContent START ===");
      console.log("Input pages:", pages);

      setIsProcessing(true);
      try {
        const allContent = pages
          .sort((a, b) => a.pageNumber - b.pageNumber)
          .map((page) => page.content)
          .join("");

        console.log(
          "All content length for redistribution:",
          allContent.length
        );

        const measurer = document.createElement("div");
        measurer.style.cssText = `
          position: absolute;
          top: -9999px;
          left: -9999px;
          width: ${LETTER_WIDTH_PX - margins.left - margins.right}px;
          visibility: hidden;
          padding: ${margins.top}px ${margins.right}px ${margins.bottom}px ${
          margins.left
        }px;
          box-sizing: border-box;
        `;
        measurer.className = `resume-template-container template-${templateId}`;
        measurer.innerHTML = `<div class="ProseMirror">${allContent}</div>`;
        document.body.appendChild(measurer);

        const styleEl = document.createElement("style");
        styleEl.textContent = selectedTemplate.styles;
        document.head.appendChild(styleEl);

        await new Promise((resolve) => setTimeout(resolve, 50));

        const proseMirror = measurer.querySelector(".ProseMirror");
        const elements = Array.from(proseMirror.children);

        console.log("Total elements to distribute:", elements.length);

        const pageBuffer = -180;
        const usableHeight =
          LETTER_HEIGHT_PX - margins.top - margins.bottom - pageBuffer;

        console.log("Usable height per page:", usableHeight);

        const newPages = [];
        let currentPageElements = [];
        let currentPageHeight = 0;
        let pageNumber = 1;

        for (let i = 0; i < elements.length; i++) {
          const element = elements[i];
          const nextElement = elements[i + 1];
          const elementHeight = element.offsetHeight;
          const styles = window.getComputedStyle(element);
          const marginTop = parseFloat(styles.marginTop) || 0;
          const marginBottom = parseFloat(styles.marginBottom) || 0;
          const totalHeight = elementHeight + marginTop + marginBottom;
          const isSection = element.tagName.toLowerCase() === "h3";
          const isJobHeader =
            element.className && element.className.includes("job-header");

          // Keep job headers together with content
          if (isJobHeader && nextElement) {
            let tempHeight = totalHeight;
            for (let j = i + 1; j < Math.min(i + 3, elements.length); j++) {
              const lookElement = elements[j];
              const lookHeight = lookElement.offsetHeight;
              const lookStyles = window.getComputedStyle(lookElement);
              const lookMarginTop = parseFloat(lookStyles.marginTop) || 0;
              const lookMarginBottom = parseFloat(lookStyles.marginBottom) || 0;
              tempHeight += lookHeight + lookMarginTop + lookMarginBottom;
            }
            if (
              currentPageHeight + tempHeight > usableHeight - 30 &&
              currentPageElements.length > 0
            ) {
              const pageContent = currentPageElements
                .map((el) => el.outerHTML)
                .join("");
              newPages.push({
                pageNumber: pageNumber++,
                content: pageContent,
                height: currentPageHeight,
              });
              currentPageElements = [element];
              currentPageHeight = totalHeight;
              continue;
            }
          }

          // Keep sections together with at least one content item
          if (isSection && nextElement) {
            const nextHeight = nextElement.offsetHeight;
            const nextStyles = window.getComputedStyle(nextElement);
            const nextMarginTop = parseFloat(nextStyles.marginTop) || 0;
            const nextMarginBottom = parseFloat(nextStyles.marginBottom) || 0;
            const minSectionHeight =
              totalHeight + nextHeight + nextMarginTop + nextMarginBottom;
            if (
              currentPageHeight + minSectionHeight > usableHeight &&
              currentPageElements.length > 0
            ) {
              const pageContent = currentPageElements
                .map((el) => el.outerHTML)
                .join("");
              newPages.push({
                pageNumber: pageNumber++,
                content: pageContent,
                height: currentPageHeight,
              });
              currentPageElements = [element];
              currentPageHeight = totalHeight;
              continue;
            }
          }

          // Add element to current page or start new page
          if (
            currentPageHeight + totalHeight > usableHeight &&
            currentPageElements.length > 0
          ) {
            const pageContent = currentPageElements
              .map((el) => el.outerHTML)
              .join("");
            newPages.push({
              pageNumber: pageNumber++,
              content: pageContent,
              height: currentPageHeight,
            });
            console.log(
              `Page ${pageNumber - 1} created with ${
                currentPageElements.length
              } elements`
            );
            currentPageElements = [element];
            currentPageHeight = totalHeight;
          } else {
            currentPageElements.push(element);
            currentPageHeight += totalHeight;
          }
        }

        // Add remaining elements as last page
        if (currentPageElements.length > 0) {
          const pageContent = currentPageElements
            .map((el) => el.outerHTML)
            .join("");
          newPages.push({
            pageNumber: pageNumber,
            content: pageContent,
            height: currentPageHeight,
          });
          console.log(
            `Final page ${pageNumber} created with ${currentPageElements.length} elements`
          );
        }

        // Ensure at least one page exists
        if (newPages.length === 0) {
          newPages.push({
            pageNumber: 1,
            content: "",
            height: 0,
          });
        }

        document.body.removeChild(measurer);
        document.head.removeChild(styleEl);

        console.log("New pages after redistribution:", newPages.length);
        console.log(
          "New pages structure:",
          newPages.map((p) => ({
            pageNumber: p.pageNumber,
            contentLength: p.content.length,
            height: p.height,
          }))
        );

        if (JSON.stringify(newPages) !== JSON.stringify(pages)) {
          console.log("Pages have changed, updating state");
          setPageContents(newPages);
          console.log("Calling onPagesDataChange with redistributed pages");
          onPagesDataChange?.(newPages);
        } else {
          console.log("No change in pages after redistribution");
        }
      } catch (error) {
        console.error("Error redistributing content:", error);
      } finally {
        setIsProcessing(false);
        console.log("=== PdfEditorComponent: redistributeContent END ===");
      }
    },
    [selectedTemplate, templateId, margins, onPagesDataChange, isMobile]
  );

  const handleSelectionUpdate = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
  }, []);

  useEffect(() => {
    console.log("=== PdfEditorComponent: Initial content effect ===");
    console.log(
      "initialContent:",
      initialContent ? initialContent.substring(0, 150) + "..." : "null"
    );
    console.log("pageContents:", pageContents);

    if (
      initialContent &&
      pageContents.length === 1 &&
      !pageContents[0].content
    ) {
      console.log("Setting initial content and triggering redistribution");
      setPageContents([
        {
          pageNumber: 1,
          content: initialContent,
          height: 0,
        },
      ]);

      // Only redistribute on desktop
      if (!isMobile) {
        setTimeout(
          () =>
            redistributeContent([
              {
                pageNumber: 1,
                content: initialContent,
                height: 0,
              },
            ]),
          100
        );
      }
    }
  }, [initialContent, pageContents, redistributeContent, isMobile]);

  useEffect(() => {
    return () => {
      if (measureTimeoutRef.current) {
        clearTimeout(measureTimeoutRef.current);
      }
    };
  }, []);

  // Log whenever pageContents changes
  useEffect(() => {
    console.log("=== PdfEditorComponent: pageContents updated ===");
    console.log("Current pageContents:", pageContents);
    console.log("Number of pages:", pageContents.length);
  }, [pageContents]);

  const totalPages = pageContents.length;

  // Determine page dimensions based on mobile/desktop
  const getPageStyle = (isCurrentPage) => {
    if (isMobile) {
      return {
        width: "calc(100vw - 20px)",
        maxWidth: "100%",
        height: "auto",
        minHeight: "600px",
        margin: "0 auto",
        backgroundColor: "#ffffff",
        boxShadow: isCurrentPage
          ? "0 0 0 2px #3b82f6"
          : "0 2px 4px rgba(0, 0, 0, 0.1)",
        position: "relative",
        transition: "box-shadow 0.3s ease",
      };
    }

    // Desktop styles (unchanged)
    return {
      width: `${LETTER_WIDTH_PX}px`,
      minHeight: `${LETTER_HEIGHT_PX}px`,
      margin: "0 auto",
      backgroundColor: "#ffffff",
      boxShadow: isCurrentPage
        ? "0 0 0 2px #3b82f6, 0 0 20px rgba(59, 130, 246, 0.2)"
        : "0 0 10px rgba(0,0,0,0.1), 0 0 30px rgba(0,0,0,0.05)",
      position: "relative",
      transition: "box-shadow 0.3s ease",
    };
  };

  return (
    <div
      className={`pdf-editor-component-container ${
        isMobileFullscreen ? "mobile-fullscreen" : ""
      }`}
    >
      {/* Exit fullscreen button - only visible in fullscreen mode */}
      {isMobile && isMobileFullscreen && (
        <button
          className="mobile-fullscreen-close"
          onClick={toggleFullscreen}
          aria-label="Exit fullscreen mode"
          style={{
            position: "fixed",
            top: "10px",
            right: "10px",
            zIndex: 10001,
            background: "rgba(0, 0, 0, 0.8)",
            color: "white",
            border: "none",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <X size={20} />
        </button>
      )}

      {/* Top toolbar/info bar */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 px-4 py-2 mb-4 flex justify-between items-center print:hidden">
        <div className="text-sm text-gray-600">
          Editing Page {currentPage} of {totalPages}
        </div>
        {totalPages > 1 && !isMobile && (
          <div className="text-xs text-orange-600 flex items-center gap-2">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Content spans {totalPages} pages
          </div>
        )}
        {!isMobile && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                console.log("=== Manual redistribution triggered ===");
                setIsProcessing(true);
                redistributeContent(pageContents).finally(() =>
                  setIsProcessing(false)
                );
              }}
              disabled={isProcessing}
              className="text-xs px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isProcessing ? "Checking..." : "Check Pages"}
            </button>
            <div className="text-xs text-gray-500">US Letter (8.5" × 11")</div>
          </div>
        )}
      </div>

      {/* Editor container */}
      <div
        className="editor-page-container"
        style={{
          margin: "0 auto",
          backgroundColor: "#f3f4f6",
          padding: isMobile ? "10px" : "20px",
          minHeight: isMobile ? "auto" : "100vh",
        }}
      >
        <div className="pages-wrapper" style={{ position: "relative" }}>
          <style>{selectedTemplate.styles}</style>
          <style>{`
            .template-${templateId} .tiptap-editor-content .ProseMirror {
              padding: ${
                isMobile
                  ? "20px"
                  : `${margins.top}px ${margins.right}px ${margins.bottom}px ${margins.left}px`
              } !important;
              min-height: ${
                isMobile
                  ? "600px"
                  : `${LETTER_HEIGHT_PX - margins.top - margins.bottom + 102}px`
              };
              max-height: ${
                isMobile
                  ? "none"
                  : `${LETTER_HEIGHT_PX - margins.top - margins.bottom + 102}px`
              };
              overflow: ${isMobile ? "visible" : "hidden"};
              box-sizing: border-box;
            }
            .resume-page-wrapper.active-page {
              box-shadow: 0 0 0 2px #3b82f6, 0 0 20px rgba(59, 130, 246, 0.2);
            }
            .resume-page-wrapper .template-${templateId} h1,
            .resume-page-wrapper .template-${templateId} .ProseMirror h1 {
              text-align: center !important;
            }
            .resume-page-wrapper .template-${templateId} h1 + p,
            .resume-page-wrapper .template-${templateId} .ProseMirror h1 + p {
              text-align: center !important;
              display: block;
              width: 100%;
              margin-left: auto;
              margin-right: auto;
            }
          `}</style>

          {/* Render pages */}
          {pageContents.map((page, pageIndex) => {
            const isCurrentPage = page.pageNumber === currentPage;
            return (
              <React.Fragment key={`page-${page.pageNumber}`}>
                <div
                  className="page-wrapper"
                  style={{
                    marginBottom: pageIndex < totalPages - 1 ? "0" : "20px",
                    position: "relative",
                  }}
                  ref={(el) => (containerRefs.current[page.pageNumber] = el)}
                >
                  <div
                    className={`resume-page-wrapper template-${
                      selectedTemplate.id
                    } ${isCurrentPage ? "active-page" : ""}`}
                    style={getPageStyle(isCurrentPage)}
                  >
                    <div
                      className={`absolute top-4 right-4 ${
                        isCurrentPage ? "bg-blue-500" : "bg-gray-500"
                      } text-white px-3 py-1 text-xs font-medium rounded-full print:hidden shadow-lg transition-colors`}
                      style={{ zIndex: 10 }}
                    >
                      Page {page.pageNumber}
                    </div>
                    <PageEditor
                      pageNumber={page.pageNumber}
                      content={page.content}
                      onContentChange={handlePageContentChange}
                      isLoading={isLoading || isProcessing}
                      templateId={templateId}
                      selectedTemplate={selectedTemplate}
                      margins={margins}
                      isFirstPage={pageIndex === 0}
                      onSelectionUpdate={handleSelectionUpdate}
                      isMobile={isMobile}
                      onToggleFullscreen={toggleFullscreen}
                      isFullscreen={isMobileFullscreen}
                    />
                  </div>
                </div>
                {pageIndex < totalPages - 1 && !isMobile && (
                  <div
                    className="page-break-indicator print:hidden"
                    style={{
                      margin: "20px auto",
                      maxWidth: `${LETTER_WIDTH_PX}px`,
                      position: "relative",
                      height: "60px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t-2 border-dashed border-gray-300"></div>
                    </div>
                    <div className="relative bg-gray-100 px-4 py-2 rounded-full text-xs font-medium text-gray-600 shadow-sm">
                      Page Break
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default PdfEditorComponent;
